import React, { useEffect, useState } from 'react';
import {
  Spin,
  Card,
  Row,
  Col,
  Typography,
  Table,
  Select,
  Button,
  Grid,
  Space,
  Avatar,
  Tag,
  Statistic,
  Tooltip,
  Progress
} from 'antd';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { dashboardsService } from '../../common/services';
import {
  EyeOutlined,
  SyncOutlined,
  CarOutlined,
  UserOutlined,
  TeamOutlined,
  RiseOutlined,
  DollarOutlined,
  SwapOutlined,
  GiftOutlined,
  SettingOutlined,
  DashboardOutlined,
  RightOutlined,
  CalendarOutlined,
  AlertOutlined,
  PhoneOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { vehiculesServices } from '../../common/services';
import { promotionsServices } from '../../common/services';
import { transactionsService } from '../../common/services';
import { partenairesService } from '../../common/services';
import Can from '../../components/can';

const { Text } = Typography;
const { Option } = Select;

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  let local = phone.replace('+225', '').trim();
  return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

const getAvatarColor = (name) => {
  const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#eb2f96', '#52c41a'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (firstName, lastName) => {
  const f = (firstName || '').charAt(0).toUpperCase();
  const l = (lastName || '').charAt(0).toUpperCase();
  return l + f || 'U';
};

const Home = () => {
  const [partenaireRecent, setPartenaireRecent] = useState(null);
  const [partenaireInscriptionParJour, setPartenaireInscriptionParJour] = useState([]);
  const [partenairesYandex, setPartenairesYandex] = useState(null);
  const [withVehicle, setWithVehicle] = useState([]);
  const [withoutVehicle, setWithoutVehicle] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [periodeGraphiquePromo, setPeriodeGraphiquePromo] = useState('jour');
  const [periodeGraphiqueVehicule, setPeriodeGraphiqueVehicule] = useState('jour');
  const [dataPromo, setDataPromo] = useState([]);
  const [dataVehicules, setDataVehicules] = useState([]);

  const [periodeGraphiqueSosCredit, setPeriodeGraphiqueSosCredit] = useState('jour');
  const [periodeGraphiqueTransferts, setPeriodeGraphiqueTransferts] = useState('jour');
  const [dataSosCredit, setDataSosCredit] = useState([]);
  const [dataTransferts, setDataTransferts] = useState([]);
  const [loadingSosCredit, setLoadingSosCredit] = useState(false);
  const [loadingTransferts, setLoadingTransferts] = useState(false);

  const [periodeGraphiquePartenaires, setPeriodeGraphiquePartenaires] = useState('jour');
  const [dataInscriptionsPartenaires, setDataInscriptionsPartenaires] = useState([]);
  const [loadingPartenaires, setLoadingPartenaires] = useState(false);

  const [periodeGraphiqueClasses, setPeriodeGraphiqueClasses] = useState('jour');
  const [dataClassesServices, setDataClassesServices] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  const [syncStats, setSyncStats] = useState({ tigoonCount: 0, yandexCount: 0 });

  const [periodeOps, setPeriodeOps] = useState('jour');
  const [opsData, setOpsData] = useState([]);
  const [loadingOps, setLoadingOps] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;
  const chartH = isMobile ? 220 : 280;
  const controlSize = isMobile ? 'small' : 'middle';

  const columns = [
    {
      title: 'Partenaire',
      key: 'user',
      render: (_, record) => {
        const fullName = `${record.lastName || ''} ${record.firstName || ''}`.trim();
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar size={32} style={{ backgroundColor: getAvatarColor(fullName), fontWeight: 600, fontSize: 12 }}>
              {getInitials(record.firstName, record.lastName)}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{fullName}</div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{formatPhoneNumber(record.phone)}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Date inscription',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Tag color="blue" style={{ borderRadius: 8, fontSize: 11 }}>
          {new Date(date).toLocaleDateString('fr-FR')}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 70,
      render: (_, record) => (
        <Tooltip title="Voir détails">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/dashboard/partenaire/${record._id}`)}
            style={{ borderRadius: 6, background: 'linear-gradient(135deg, #fa8c16, #ffa940)', border: 'none' }}
          />
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    async function fetchData() {
      try {
        const [recent, daily, yandex, w, wo, promotionsStats, vehiculesStats, yandexVsTigoon] = await Promise.all([
          dashboardsService.getPartenairesRecent(),
          dashboardsService.getPartenaireInscriptionParJour(),
          dashboardsService.getPartenaireYandex(),
          dashboardsService.getPartenairesThisWeekWithVehicle(),
          dashboardsService.getPartenairesThisWeekWithoutVehicle(),
          promotionsServices.statsPromotionsDimanche(periodeGraphiquePromo),
          vehiculesServices.statsInscriptionsVehicules(periodeGraphiqueVehicule),
          dashboardsService.getYandexVsTigoon(),
        ]);
        setPartenaireRecent(recent);
        setPartenaireInscriptionParJour(daily);
        setPartenairesYandex(yandex?.count || 0);
        setWithVehicle(w);
        setWithoutVehicle(wo);
        setDataPromo(promotionsStats.data || []);
        setDataVehicules(vehiculesStats.data || []);
        setSyncStats({ tigoonCount: yandexVsTigoon?.tigoonCount || 0, yandexCount: yandexVsTigoon?.yandexCount || 0 });
      } catch (error) {
        console.error("Erreur chargement dashboard :", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [periodeGraphiquePromo, periodeGraphiqueVehicule]);

  useEffect(() => {
    (async () => {
      setLoadingSosCredit(true);
      try {
        const res = await transactionsService.statsSosCredit(periodeGraphiqueSosCredit);
        setDataSosCredit(res.data || []);
      } catch (e) { console.error(e); } finally { setLoadingSosCredit(false); }
    })();
  }, [periodeGraphiqueSosCredit]);

  useEffect(() => {
    (async () => {
      setLoadingTransferts(true);
      try {
        const res = await transactionsService.statsTransferts(periodeGraphiqueTransferts);
        setDataTransferts(res.data || []);
      } catch (e) { console.error(e); } finally { setLoadingTransferts(false); }
    })();
  }, [periodeGraphiqueTransferts]);

  useEffect(() => {
    (async () => {
      setLoadingPartenaires(true);
      try {
        const data = await partenairesService.getStatsInscriptions(periodeGraphiquePartenaires);
        setDataInscriptionsPartenaires(data || []);
      } catch (e) { console.error(e); } finally { setLoadingPartenaires(false); }
    })();
  }, [periodeGraphiquePartenaires]);

  useEffect(() => {
    (async () => {
      setLoadingClasses(true);
      try {
        const data = await vehiculesServices.statsClassesServices(periodeGraphiqueClasses);
        setDataClassesServices(data || []);
      } catch (e) { console.error(e); } finally { setLoadingClasses(false); }
    })();
  }, [periodeGraphiqueClasses]);

  useEffect(() => {
    (async () => {
      setLoadingOps(true);
      try {
        const res = await dashboardsService.getOperationsStats(periodeOps);
        setOpsData(res.data || []);
      } catch (e) { console.error(e); } finally { setLoadingOps(false); }
    })();
  }, [periodeOps]);

  const formatDataByCategorie = (rawData) => {
    const lastDate = rawData.length > 0 ? rawData.map(item => item._id.periode).sort().reverse()[0] : null;
    const filtered = rawData.filter(item => item._id.periode === lastDate);
    return filtered.sort((a, b) => b.total - a.total).slice(0, 8).map(item => ({ categorie: item._id.categorie, total: item.total }));
  };
  const formattedCategorieData = formatDataByCategorie(dataClassesServices);

  if (loading || !partenaireRecent) {
    return (
      <div style={{ padding: 40, textAlign: 'center', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="Chargement du tableau de bord..." />
      </div>
    );
  }

  const renderSummary = (data, label) => {
    const total = data.reduce((sum, item) => sum + (item.count || 0), 0);
    return `${label} : ${total.toLocaleString('fr-FR')}`;
  };

  const totalYandex = syncStats.yandexCount;
  const synced = Math.min(syncStats.tigoonCount, totalYandex);
  const remaining = Math.max(totalYandex - synced, 0);
  const percent = totalYandex > 0 ? Math.round((synced / totalYandex) * 100) : 0;
  const donutData = [{ name: 'Synchronisés', value: synced }, { name: 'Restants', value: remaining }];
  const donutColors = ['#52c41a', '#ff4d4f'];

  const formatPeriodeTick = (v) => {
    const d = new Date(v);
    if (periodeOps === 'mois') return d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    if (periodeOps === 'annee') return d.getFullYear();
    return d.toLocaleDateString('fr-FR');
  };

  const tooltipLabel = (v) => {
    const d = new Date(v);
    if (periodeOps === 'annee') return `Année ${d.getFullYear()}`;
    if (periodeOps === 'mois') return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    return d.toLocaleString('fr-FR');
  };

  const COLORS = { sos_credit: '#13c2c2', transfert: '#722ed1', changement_numero: '#fa8c16', promo_dimanche: '#f5222d', vehicule_modifie: '#52c41a' };

  // Calculer les totaux des opérations du jour
  const today = new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD
  const todayData = opsData.find(item => {
    const itemDate = new Date(item.periode).toISOString().split('T')[0];
    return itemDate === today;
  });

  const opsTotals = {
    sos_credit: todayData?.byType?.sos_credit || 0,
    transfert: todayData?.byType?.transfert || 0,
    changement_numero: todayData?.byType?.changement_numero || 0,
    promo_dimanche: todayData?.byType?.promo_dimanche || 0,
    vehicule_modifie: todayData?.byType?.vehicule_modifie || 0,
  };

  const PeriodSelect = ({ value, onChange }) => (
    <Select size={controlSize} value={value} onChange={onChange} style={{ width: 100, borderRadius: 8 }}>
      <Option value="jour">Jour</Option>
      <Option value="semaine">Semaine</Option>
      <Option value="mois">Mois</Option>
      <Option value="annee">Année</Option>
    </Select>
  );

  return (
    <div className="content-wrapper">
      <style>
        {`
          .dashboard-card { border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); transition: all 0.3s; }
          .dashboard-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
          .dashboard-card .ant-card-head { border-bottom: 2px solid #f0f0f0; }
          .dashboard-card .ant-card-head-title { font-weight: 600; color: #262626; }
          .stat-mini { border-radius: 12px; transition: all 0.3s; }
          .stat-mini:hover { transform: translateY(-2px); }
          .drivers-table .ant-table-row:nth-child(odd) { background-color: #fafafa; }
          .drivers-table .ant-table-row:hover > td { background-color: #fff7e6 !important; }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 16, paddingLeft: isMobile ? 12 : 24, paddingRight: isMobile ? 12 : 24 }}>
        {/* Quick Stats - Totaux des opérations */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.sos_credit}, #36cfc9)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>SOS Crédit</span>} value={opsTotals.sos_credit} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<AlertOutlined />} />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.transfert}, #b37feb)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>Transferts UV</span>} value={opsTotals.transfert} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<SwapOutlined />} />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.changement_numero}, #ffc069)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>Chgt numéro</span>} value={opsTotals.changement_numero} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<PhoneOutlined />} />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.promo_dimanche}, #ff7875)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>Promo dim.</span>} value={opsTotals.promo_dimanche} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<GiftOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={16} lg={8}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.vehicule_modifie}, #95de64)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Row align="middle" gutter={16}>
                <Col flex="auto">
                  <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>Véhicules modifiés</span>} value={opsTotals.vehicule_modifie} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<ToolOutlined />} />
                </Col>
                <Col>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>Sync: {percent}%</div>
                  <Progress percent={percent} strokeColor="white" trailColor="rgba(255,255,255,0.2)" showInfo={false} size="small" style={{ width: 60 }} />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Sync Donut + Tables */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={8}>
            <Card className="dashboard-card" title={<span><SyncOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Synchronisation Yandex / TIGOON</span>}>
              <div style={{ textAlign: 'center' }}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} startAngle={90} endAngle={-270} paddingAngle={2}>
                      {donutData.map((_, idx) => <Cell key={idx} fill={donutColors[idx]} />)}
                    </Pie>
                    <RechartsTooltip formatter={(val) => val.toLocaleString('fr-FR')} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 18 }}>{synced.toLocaleString('fr-FR')} / {totalYandex.toLocaleString('fr-FR')}</div>
                  <Space style={{ marginTop: 8 }}>
                    <Tag color="green"><span style={{ width: 8, height: 8, background: '#52c41a', borderRadius: 2, display: 'inline-block', marginRight: 4 }} />Synchronisés</Tag>
                    <Tag color="red"><span style={{ width: 8, height: 8, background: '#ff4d4f', borderRadius: 2, display: 'inline-block', marginRight: 4 }} />Restants</Tag>
                  </Space>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card className="dashboard-card" title={<span><CarOutlined style={{ color: '#52c41a', marginRight: 8 }} />Chauffeurs avec véhicule</span>} extra={<Link to="/dashboard/partenaires"><Button type="link" size="small" icon={<RightOutlined />}>Voir tout</Button></Link>} style={{ marginBottom: 16 }}>
              <Table className="drivers-table" dataSource={withVehicle} columns={columns} rowKey="_id" pagination={{ pageSize: 2, hideOnSinglePage: true }} size="small" scroll={{ x: true }} locale={{ emptyText: 'Aucun chauffeur' }} />
            </Card>
            <Card className="dashboard-card" title={<span><UserOutlined style={{ color: '#faad14', marginRight: 8 }} />Chauffeurs sans véhicule</span>} extra={<Link to="/dashboard/partenaires"><Button type="link" size="small" icon={<RightOutlined />}>Voir tout</Button></Link>}>
              <Table className="drivers-table" dataSource={withoutVehicle} columns={columns} rowKey="_id" pagination={{ pageSize: 2, hideOnSinglePage: true }} size="small" scroll={{ x: true }} locale={{ emptyText: 'Aucun chauffeur' }} />
            </Card>
          </Col>
        </Row>

        {/* Charts Row 1 */}
        <Can perm="promotions:stats">
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={8}>
              <Card className="dashboard-card" title={<span><GiftOutlined style={{ color: '#fa541c', marginRight: 8 }} />Activations promo dimanche</span>} extra={<PeriodSelect value={periodeGraphiquePromo} onChange={setPeriodeGraphiquePromo} />}>
                <ResponsiveContainer width="100%" height={chartH}>
                  <BarChart data={dataPromo}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#fa541c" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} lg={16}>
              <Card className="dashboard-card" title={<span><RiseOutlined style={{ color: '#1890ff', marginRight: 8 }} />Évolution des opérations</span>} extra={<PeriodSelect value={periodeOps} onChange={setPeriodeOps} />}>
                <ResponsiveContainer width="100%" height={chartH}>
                  {loadingOps ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                    <BarChart data={opsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="periode" tickFormatter={formatPeriodeTick} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RechartsTooltip labelFormatter={tooltipLabel} />
                      <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
                      <Bar dataKey="byType.sos_credit" name="SOS Crédit" stackId="ops" fill={COLORS.sos_credit} />
                      <Bar dataKey="byType.transfert" name="Transfert" stackId="ops" fill={COLORS.transfert} />
                      <Bar dataKey="byType.changement_numero" name="Chgt numéro" stackId="ops" fill={COLORS.changement_numero} />
                      <Bar dataKey="byType.promo_dimanche" name="Promo dim." stackId="ops" fill={COLORS.promo_dimanche} />
                      <Bar dataKey="byType.vehicule_modifie" name="Véhicule mod." stackId="ops" fill={COLORS.vehicule_modifie} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        </Can>

        {/* Charts Row 2 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12} lg={8}>
            <Card className="dashboard-card" title={<Link to="/dashboard/partenaires" style={{ color: '#262626' }}><TeamOutlined style={{ color: '#13c2c2', marginRight: 8 }} />Inscriptions partenaires</Link>} extra={<PeriodSelect value={periodeGraphiquePartenaires} onChange={setPeriodeGraphiquePartenaires} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                {loadingPartenaires ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                  <BarChart data={dataInscriptionsPartenaires}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="total" fill="#13c2c2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Card className="dashboard-card" title={<Link to="/dashboard/vehicules" style={{ color: '#262626' }}><CarOutlined style={{ color: '#52c41a', marginRight: 8 }} />Inscriptions véhicules</Link>} extra={<PeriodSelect value={periodeGraphiqueVehicule} onChange={setPeriodeGraphiqueVehicule} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                <BarChart data={dataVehicules}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#52c41a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} md={24} lg={8}>
            <Card className="dashboard-card" title={<span><SettingOutlined style={{ color: '#1890ff', marginRight: 8 }} />Catégories véhicules</span>} extra={<PeriodSelect value={periodeGraphiqueClasses} onChange={setPeriodeGraphiqueClasses} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                {loadingClasses ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                  <BarChart data={formattedCategorieData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="categorie" tick={{ fontSize: 9, angle: -30, textAnchor: 'end' }} interval={0} tickMargin={8} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="total" fill="#1890ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Charts Row 3 - SOS Credit & Transfers */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card className="dashboard-card" title={<Link to="/dashboard/historique-sos-credit" style={{ color: '#262626' }}><DollarOutlined style={{ color: '#1890ff', marginRight: 8 }} />Statistiques SOS Crédit</Link>} extra={<PeriodSelect value={periodeGraphiqueSosCredit} onChange={setPeriodeGraphiqueSosCredit} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                {loadingSosCredit ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                  <BarChart data={dataSosCredit}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#1890ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#f6f8fa', borderRadius: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{renderSummary(dataSosCredit, 'Total SOS Crédit')}</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="dashboard-card" title={<Link to="/dashboard/historique-transferts" style={{ color: '#262626' }}><SwapOutlined style={{ color: '#722ed1', marginRight: 8 }} />Statistiques Transferts</Link>} extra={<PeriodSelect value={periodeGraphiqueTransferts} onChange={setPeriodeGraphiqueTransferts} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                {loadingTransferts ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                  <BarChart data={dataTransferts}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#722ed1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#f6f8fa', borderRadius: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{renderSummary(dataTransferts, 'Total Transferts UV')}</Text>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Home;
